import { ArrowRight, Grid2X2, LoaderCircle, Package, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@sshop/i18n';
import { cn } from '@sshop/ui';
import type { Money } from '@sshop/domain';
import { formatMoney } from '@/entities-ui/product';
import { useLocale } from '@/shared/hooks/useLocale';
import { routes } from '@/shared/lib/routes';
import { headerSearchPanelStyles as s } from './HeaderSearchPanel.styles';
import type { SearchPanelProps } from './HeaderSearchPanel.types';

const sections = ['products', 'collections'] as const;

export function HeaderSearchResults(props: SearchPanelProps) {
  const { t } = useTranslation();
  const { t: productText } = useTranslation('product');
  const locale = useLocale();
  const number = (value: number) => value.toLocaleString(locale === 'ge' ? 'ka-GE' : 'en-US');
  const money = (amount: number): Money => ({ amount, currency: 'GEL' });
  const groups = {
    products: {
      loading: props.loading,
      failed: props.failed,
      total: props.total,
      retry: props.onRetry,
      url: routes.search(locale, props.query),
      items: props.products.map((product) => ({
        id: product.id,
        title: product.title,
        url: routes.product(locale, product.slug),
        image: product.image?.url,
        price: product.price,
        original: product.compareAtPrice,
        inStock: product.inStock,
        description: undefined as string | undefined,
      })),
    },
    collections: {
      loading: props.collectionsLoading,
      failed: props.collectionsFailed,
      total: props.collectionsTotal,
      retry: props.onRetryCollections,
      url: props.collectionsUrl,
      items: props.collections.map((collection) => ({
        id: collection.id,
        title: collection.title,
        url: routes.collection(locale, collection.slug),
        image: collection.imageUrl,
        price: money(collection.price),
        original: money(collection.compareAtPrice),
        inStock: collection.availableQuantity > 0,
        description: t('collection.productCount', { count: collection.productCount }),
      })),
    },
  };

  return (
    <div ref={props.panelRef} className={s.resultsPanel} id="header-search-suggestions">
      <div className={s.resultsColumns}>
        {(!props.mobile || props.matches.length > 0 || props.brands.length > 0) && (
          <aside data-search-scroll="filters" className={s.resultsAside}>
            <div id="header-search-filters" className="space-y-4">
              {props.matches.length > 0 && (
                <section aria-label={t('search.categories')} className="min-w-0">
                  <h2 className={cn(s.heading, props.mobile && 'sr-only')}>
                    {t('search.categories')}
                  </h2>
                  <div className={s.categoryMatches}>
                    {props.matches.slice(0, 5).map((category) => (
                      <Link
                        key={category.id}
                        to={`${routes.search(locale, props.query)}&category_ids=${encodeURIComponent(category.id)}`}
                        className={s.categoryMatch}
                        onClick={props.onNavigate}
                      >
                        <Grid2X2 className={s.iconGlyph} aria-hidden />
                        <span className={s.categoryMatchName}>{category.name}</span>
                        <span className={s.count}>{number(category.count)}</span>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
              {props.brands.length > 0 && (
                <section aria-label={t('search.brands')} className="min-w-0">
                  <h2 className={s.heading}>{t('search.brands')}</h2>
                  <div className={s.chips}>
                    {props.brands.slice(0, 6).map((brand) => (
                      <Link
                        key={brand.id}
                        to={`${routes.search(locale, props.query)}&brand=${encodeURIComponent(brand.id)}`}
                        className={s.chip}
                        onClick={props.onNavigate}
                      >
                        {brand.label}
                      </Link>
                    ))}
                  </div>
                </section>
              )}
              {props.matches.length === 0 && props.brands.length === 0 && (
                <p className={s.empty}>{t('search.refineHint')}</p>
              )}
            </div>
          </aside>
        )}
        {props.mobile && (
          <div
            role="tablist"
            aria-label={t('nav.search')}
            className="border-border-subtle bg-bg-canvas sticky top-0 z-10 grid shrink-0 grid-cols-2 border-b px-4"
          >
            {sections.map((section, index) => (
              <button
                key={section}
                type="button"
                role="tab"
                id={`header-search-${section}-tab`}
                aria-selected={props.resultSection === section}
                aria-controls={`header-search-${section}-panel`}
                tabIndex={props.resultSection === section ? 0 : -1}
                className="min-h-12 text-fg-muted aria-selected:border-accent aria-selected:text-accent focus-visible:ring-accent border-b-2 border-transparent px-2 py-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                onClick={() => props.onSelectSection(section)}
                onKeyDown={(event) => {
                  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                  event.preventDefault();
                  const next =
                    sections[event.key === 'Home' ? 0 : event.key === 'End' ? 1 : 1 - index]!;
                  props.onSelectSection(next);
                  document.getElementById(`header-search-${next}-tab`)?.focus();
                }}
              >
                {t(`search.${section}`)}
                {!groups[section].loading &&
                !groups[section].failed &&
                groups[section].total !== undefined
                  ? ` (${number(groups[section].total!)})`
                  : ''}
              </button>
            ))}
          </div>
        )}
        {sections
          .filter((section) => !props.mobile || props.resultSection === section)
          .map((section) => {
            const group = groups[section];
            const offset = !props.mobile && section === 'collections' ? props.products.length : 0;
            return (
              <section
                key={section}
                data-search-scroll={section}
                className={cn(
                  s.resultsMain,
                  section === 'collections' && 'lg:border-border-subtle lg:border-l',
                )}
                id={`header-search-${section}-panel`}
                role={props.mobile ? 'tabpanel' : undefined}
                aria-labelledby={
                  props.mobile ? `header-search-${section}-tab` : `header-search-${section}-heading`
                }
              >
                {!props.mobile && (
                  <div className={s.sectionHeader}>
                    <h2 id={`header-search-${section}-heading`} className={s.headingTitle}>
                      {t(`search.${section}`)}
                    </h2>
                    {!group.loading && !group.failed && group.total !== undefined && (
                      <span className={s.count}>{number(group.total)}</span>
                    )}
                  </div>
                )}
                <div role="status" aria-live="polite" aria-atomic="true">
                  {group.loading ? (
                    <div className={s.status}>
                      <LoaderCircle className={s.spinner} aria-hidden />
                      {t('search.loading')}
                    </div>
                  ) : group.failed ? (
                    <div className={s.status}>
                      <Search className={s.iconGlyph} aria-hidden />
                      {t('search.error')}
                      <button type="button" className={s.chip} onClick={group.retry}>
                        {t('app.retry')}
                      </button>
                    </div>
                  ) : group.items.length === 0 ? (
                    <div className={s.status}>
                      <Search className={s.iconGlyph} aria-hidden />
                      {t(section === 'collections' ? 'search.emptyCollections' : 'search.empty')}
                    </div>
                  ) : null}
                </div>
                <ul
                  id={`header-search-${section}-options`}
                  className={s.list}
                  role="listbox"
                  aria-label={t(`search.${section}`)}
                >
                  {!group.loading &&
                    !group.failed &&
                    group.items.map((item, index) => {
                      const discounted = item.original && item.original.amount > item.price.amount;
                      return (
                        <li key={item.id} role="presentation">
                          <Link
                            id={`header-search-option-${offset + index}`}
                            role="option"
                            aria-selected={offset + index === props.activeIndex}
                            to={item.url}
                            className={s.item}
                            onClick={props.onNavigate}
                          >
                            {item.image ? (
                              <img src={item.image} alt="" className={s.thumb} />
                            ) : (
                              <span className={cn(s.thumb, 'flex items-center justify-center')}>
                                <Package className="h-6 w-6 text-gray-400" aria-hidden />
                              </span>
                            )}
                            <span className={s.itemBody}>
                              <span className={s.itemTitle}>{item.title}</span>
                              {item.description && (
                                <span className="text-fg-muted text-xs">{item.description}</span>
                              )}
                              <span className={s.itemPrices}>
                                <span className={cn(s.itemPrice, discounted && s.salePrice)}>
                                  {formatMoney(item.price, locale)}
                                </span>
                                {discounted && (
                                  <span className={s.itemOriginalPrice}>
                                    {formatMoney(item.original!, locale)}
                                  </span>
                                )}
                                <span className={item.inStock ? s.stock : s.noStock}>
                                  {productText(item.inStock ? 'inStock' : 'outOfStock')}
                                </span>
                              </span>
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                </ul>
                {group.url && (
                  <Link to={group.url} className={s.footer} onClick={props.onNavigate}>
                    {t(section === 'collections' ? 'search.viewAllCollections' : 'search.viewAll')}
                    {!group.loading && !group.failed && group.total !== undefined && (
                      <span>({number(group.total)})</span>
                    )}
                    <ArrowRight className={s.iconGlyph} aria-hidden />
                  </Link>
                )}
              </section>
            );
          })}
      </div>
    </div>
  );
}
